import { useQuery } from '@tanstack/react-query';
import {
  getEnhancedDashboardSummary,
  getCostsOverview,
  getStatusOverview,
  getClientRevenueAnalysis,
  getServicePenetrationAnalysis,
  getGeographicDistribution,
  getClientCompletenessScores,
  getContractExpiryForecast,
  getContractValueDistribution
} from '../api/utilityServices';
import { FilterOptions } from '../pages/UtilityDashboard/components/DashboardFilters';

const buildParams = (filters: FilterOptions): Record<string, string> => {
  const params: Record<string, string> = {};
  if (filters.startDate) {
    params.startDate = filters.startDate.toISOString();
  }
  if (filters.endDate) {
    params.endDate = filters.endDate.toISOString();
  }
  if (filters.serviceTypes.length > 0) {
    params.serviceTypes = filters.serviceTypes.join(',');
  }
  return params;
};

// Service Overview (Tab 0)
export const useServiceOverviewQuery = (filters: FilterOptions) => {
  return useQuery({
    queryKey: ['dashboard', 'service-overview', filters],
    queryFn: async () => {
      const params = buildParams(filters);
      const [summary, costs, status, contractValue] = await Promise.all([
        getEnhancedDashboardSummary(params),
        getCostsOverview(params),
        getStatusOverview(params),
        getContractValueDistribution(params)
      ]);
      return { summary, costs, status, contractValue };
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchInterval: 30 * 1000, // Auto-refresh every 30 seconds
    refetchIntervalInBackground: true, // Continue refreshing when tab is in background
    refetchOnWindowFocus: true, // Refresh when user returns to tab
  });
};

// Client Insights (Tab 1)
export const useClientInsightsQuery = (filters: FilterOptions, enabled: boolean) => {
  return useQuery({
    queryKey: ['dashboard', 'client-insights', filters],
    queryFn: async () => {
      const params = buildParams(filters);
      const [clientRevenue, servicePenetration, geographic, completeness] = await Promise.all([
        getClientRevenueAnalysis(params),
        getServicePenetrationAnalysis(params),
        getGeographicDistribution(params),
        getClientCompletenessScores(params)
      ]);
      return { clientRevenue, servicePenetration, geographic, completeness };
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: enabled ? 30 * 1000 : false, // Only refresh when tab is active
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
};

// Contract Analysis (Tab 2)
export const useContractAnalysisQuery = (filters: FilterOptions, enabled: boolean) => {
  return useQuery({
    queryKey: ['dashboard', 'contract-analysis', filters],
    queryFn: async () => {
      const params = buildParams(filters);
      const contractExpiry = await getContractExpiryForecast(params);
      return { contractExpiry };
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: enabled ? 30 * 1000 : false,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
  });
};
